import { ref, computed, getCurrentInstance, onBeforeUnmount } from "vue";
import type { ComputedRef } from "vue";
import store from "@/store";
import { DataModules } from "@/store/modules/data/modules";
import { UUID } from "@/data/enums";
import { DEFAULT_LIST_SCOPE } from "@/store/modules/data";
import type { ITag, IBrand } from "@upmind-automation/types";

export interface UseTagsOptions {
  /** Brand to scope the tag list to. Defaults to UUID.ORG (no filter). */
  brandId?: IBrand["id"];
  /** Vuex list scope. Defaults to `$list` (matches TagsMixin). */
  scope?: string;
  /** Include staged imports in the request (admin import flow). */
  withStagedImports?: boolean;
}

export interface UseTagsReturn {
  tags: ComputedRef<ITag[]>;
  isLoading: ComputedRef<boolean>;
  fetch: (ignoreStored?: boolean) => Promise<void>;
}

/**
 * Listing-side counterpart to TagsMixin.
 *
 * Mirrors `TagsMixin.getTags()` for use inside Composition API setup blocks.
 * Reads from the same Vuex `data/tags` module, so existing mixin consumers
 * are unaffected. Cleans up its scope on unmount.
 */
export function useTags(options: UseTagsOptions = {}): UseTagsReturn {
  const brandId = options.brandId ?? UUID.ORG;
  const scope = options.scope ?? DEFAULT_LIST_SCOPE;
  const withStagedImports = options.withStagedImports ?? false;
  const vm = getCurrentInstance()?.proxy;

  const isFetching = ref(false);

  const tags = computed<ITag[]>(
    () =>
      store.getters[`data/listArray`]({
        storeModule: DataModules.TAGS,
        scope
      }) || []
  );

  const isLoading = computed(() => isFetching.value);

  async function fetch(ignoreStored = false): Promise<void> {
    isFetching.value = true;
    try {
      await store.dispatch(`data/${DataModules.TAGS}/list`, {
        scope,
        params: {
          limit: 0,
          offset: 0,
          order: "-name",
          ...(withStagedImports ? { with_staged_imports: 1 } : {}),
          ...(brandId !== UUID.ORG ? { "filter[brand_id]": brandId } : {})
        },
        vm,
        ignoreStored
      });
    } finally {
      isFetching.value = false;
    }
  }

  onBeforeUnmount(() => {
    store.dispatch("data/binList", {
      storeModule: DataModules.TAGS,
      scope,
      vm
    });
  });

  return { tags, isLoading, fetch };
}
